import { writesApi } from './api';
import type { Business, Branch, BusinessStaff, KycDocument, Wallet, Product } from '@apptypes/index';

export interface BusinessProductsPage {
  data: Product[];
  total: number;
  page: number;
  limit: number;
}

export interface CountryOption { id: string; name: string; isoCode2: string; currencyCode: string; }

export interface PayoutDetail {
  id: string;
  businessId: string;
  payoutMethod: string;
  details: Record<string, string>;
  label?: string | null;
  isDefault: boolean;
  status: string;
  createdAt: string;
}
export interface PayoutDetailInput {
  payoutMethod: string;
  details: Record<string, string>;
  label?: string;
  makeDefault?: boolean;
}

export interface BusinessRole {
  roleId: string;
  name: string;
  description?: string | null;
  isTemplate?: boolean;
  permissions: unknown[];
}
export interface AvailablePermission { permissionId: string; code: string; name: string; description?: string | null; }
export interface UserGrant { userPermissionId: string; permissionId: string; code: string; grantType: 'grant' | 'deny'; }

export const PAYOUT_METHODS: { code: string; label: string; fields: { key: string; label: string }[] }[] = [
  { code: 'mpesa', label: 'M-Pesa (Send Money)', fields: [{ key: 'phone', label: 'M-Pesa number' }] },
  { code: 'mpesa_paybill', label: 'M-Pesa Paybill', fields: [{ key: 'paybill_number', label: 'Paybill number' }, { key: 'account_number', label: 'Account number' }] },
  { code: 'mpesa_till', label: 'M-Pesa Till (Buy Goods)', fields: [{ key: 'till_number', label: 'Till number' }] },
  { code: 'bank_transfer', label: 'Bank account', fields: [{ key: 'bank_name', label: 'Bank name' }, { key: 'account_number', label: 'Account number' }, { key: 'account_name', label: 'Account name' }] },
];

export const countriesService = {
  list: () => writesApi.get<CountryOption[]>('/admin/countries'),
};

export const businessesService = {
  // === Businesses ===
  // Admin list → Spring Boot /admin/businesses (0-indexed; UI sends 1-indexed)
  list: (params?: { page?: number; limit?: number; status?: string; search?: string }) =>
    writesApi.get<Business[]>('/admin/businesses', {
      ...params,
      page: ((params?.page ?? 1) - 1),  // convert 1-based UI page to 0-based Spring Boot page
      size: params?.limit ?? 20,
    } as Record<string, unknown>),

  // Detail → Spring Boot /businesses/:id (owner-scoped, but admin JWT passes check)
  get: (businessId: string) =>
    writesApi.get<Business>(`/businesses/${businessId}`),

  /** Admin creates a business on behalf of an owner user */
  adminCreate: (data: {
    ownerUserId: string;
    name: string;
    phone?: string;
    email?: string;
    businessType?: string;
    description?: string;
    kraPin?: string;
    registrationNumber?: string;
    countryId?: string;
  }) => writesApi.post<Business>('/admin/businesses', data),

  update: (businessId: string, data: Partial<Business>) =>
    writesApi.put<Business>(`/businesses/${businessId}`, data),

  /** Ops edit of a business profile → PATCH /admin/businesses/:id */
  adminUpdate: (businessId: string, data: {
    name?: string; description?: string; email?: string; phone?: string;
    businessType?: string; taxType?: string; kraPin?: string; businessCategory?: string;
  }) => writesApi.patch<Business>(`/admin/businesses/${businessId}`, data),

  // === Business-scoped roles & specialized permissions for a staff member ===
  listBusinessRoles: (businessId: string) =>
    writesApi.get<BusinessRole[]>(`/businesses/${businessId}/roles`),
  listAvailablePermissions: (businessId: string) =>
    writesApi.get<AvailablePermission[]>(`/businesses/${businessId}/permissions/available`),
  listUserGrants: (businessId: string, userId: string) =>
    writesApi.get<UserGrant[]>(`/businesses/${businessId}/permissions/users/${userId}/grants`),
  assignRole: (businessId: string, userId: string, roleId: string, branchIds?: string[]) =>
    writesApi.post(`/businesses/${businessId}/permissions/users/${userId}/roles`, { roleId, branchIds }),
  removeRole: (businessId: string, userId: string, roleId: string) =>
    writesApi.delete(`/businesses/${businessId}/permissions/users/${userId}/roles/${roleId}`),
  grantPermission: (businessId: string, userId: string, permissionId: string, isDeny: boolean) =>
    writesApi.post(`/businesses/${businessId}/permissions/users/${userId}/grants`, { permissionId, isDeny }),
  revokePermission: (businessId: string, userId: string, permissionId: string) =>
    writesApi.delete(`/businesses/${businessId}/permissions/users/${userId}/grants/${permissionId}`),

  // === Payout details (where the business receives earnings) ===
  listPayouts: (businessId: string) =>
    writesApi.get<PayoutDetail[]>(`/businesses/${businessId}/payout-details`),
  addPayout: (businessId: string, body: PayoutDetailInput) =>
    writesApi.post<PayoutDetail>(`/businesses/${businessId}/payout-details`, body),
  updatePayout: (businessId: string, id: string, body: PayoutDetailInput) =>
    writesApi.put<PayoutDetail>(`/businesses/${businessId}/payout-details/${id}`, body),
  setDefaultPayout: (businessId: string, id: string) =>
    writesApi.post<PayoutDetail>(`/businesses/${businessId}/payout-details/${id}/default`),
  archivePayout: (businessId: string, id: string) =>
    writesApi.delete(`/businesses/${businessId}/payout-details/${id}`),

  // Admin lifecycle actions
  approve: (businessId: string) =>
    writesApi.post(`/admin/businesses/${businessId}/approve`),

  reject: (businessId: string, reason?: string) =>
    writesApi.post(`/admin/businesses/${businessId}/reject`, { reason }),

  suspend: (businessId: string, reason?: string) =>
    writesApi.post(`/admin/businesses/${businessId}/suspend`, { reason }),

  activate: (businessId: string) =>
    writesApi.post(`/admin/businesses/${businessId}/activate`),

  // === Branches: Spring Boot ===
  listBranches: (businessId: string) =>
    writesApi.get<Branch[]>(`/businesses/${businessId}/branches`),

  getBranch: (businessId: string, branchId: string) =>
    writesApi.get<Branch>(`/businesses/${businessId}/branches/${branchId}`),

  createBranch: (businessId: string, data: Partial<Branch>) =>
    writesApi.post<Branch>(`/businesses/${businessId}/branches`, data),

  updateBranch: (businessId: string, branchId: string, data: Partial<Branch>) =>
    writesApi.put<Branch>(`/businesses/${businessId}/branches/${branchId}`, data),

  // === Staff: Spring Boot ===
  // StaffResponse exposes the UUID as `staffId`; normalize to `id` for the UI.
  listStaff: async (businessId: string): Promise<BusinessStaff[]> => {
    const raw = await writesApi.get<any[]>(`/businesses/${businessId}/staff`);
    return (raw ?? []).map((s: any) => ({ ...s, id: s.id ?? s.staffId })) as BusinessStaff[];
  },

  addStaff: (
    businessId: string,
    data: {
      phone: string;
      staffType: string;
      firstName: string;
      lastName: string;
      email?: string;
      nationalId?: string;
      dateOfBirth?: string;
      gender?: string;
      jobTitle?: string;
      branchIds?: string[];
    },
  ) => writesApi.post(`/businesses/${businessId}/staff`, data),

  getStaff: async (businessId: string, staffId: string): Promise<BusinessStaff> => {
    const s = await writesApi.get<any>(`/businesses/${businessId}/staff/${staffId}`);
    return { ...s, id: s?.id ?? s?.staffId } as BusinessStaff;
  },

  updateStaff: async (
    businessId: string,
    staffId: string,
    data: { staffType?: string; jobTitle?: string },
  ): Promise<BusinessStaff> => {
    const s = await writesApi.put<any>(`/businesses/${businessId}/staff/${staffId}`, data);
    return { ...s, id: s?.id ?? s?.staffId } as BusinessStaff;
  },

  suspendStaff: (businessId: string, staffId: string, reason?: string) =>
    writesApi.post(`/businesses/${businessId}/staff/${staffId}/suspend`, { reason }),

  activateStaff: (businessId: string, staffId: string) =>
    writesApi.post(`/businesses/${businessId}/staff/${staffId}/activate`),

  removeStaff: (businessId: string, staffId: string) =>
    writesApi.delete(`/businesses/${businessId}/staff/${staffId}`),

  // === KYC: Spring Boot ===
  getKyc: (businessId: string) =>
    writesApi.get<KycDocument[]>(`/businesses/${businessId}/kyc`),

  submitKyc: (businessId: string, data: FormData) =>
    writesApi.post(`/businesses/${businessId}/kyc`, data),

  // === Wallets ===
  getWallets: (businessId: string) =>
    writesApi.get<Wallet[]>(`/businesses/${businessId}/wallets`),

  // === Products ===
  // Backend's ProductResponse uses @JsonProperty to remap several fields:
  //   id            → productId
  //   basePrice     → basePriceCents
  //   compareAtPrice → compareAtPriceCents
  // We restore the original names here so the rest of the app sees a stable shape.
  /**
   * Business-wide product list (all branches, drafts included). Spring Boot paginates this
   * (default 20/page) and answers { data, total, page, limit } - callers must page.
   */
  listProducts: async (
    businessId: string,
    params?: { page?: number; limit?: number; search?: string },
  ): Promise<BusinessProductsPage> => {
    const res = await writesApi.get<any>(
      `/businesses/${businessId}/products`,
      { page: 1, limit: 20, ...(params ?? {}) } as Record<string, unknown>,
    );
    const raw: any[] = Array.isArray(res) ? res : (res?.data ?? []);
    const rows = raw.map((p: any) => ({
      ...p,
      id: p.id ?? p.productId,
      basePrice: p.basePrice ?? p.basePriceCents,
      compareAtPrice: p.compareAtPrice ?? p.compareAtPriceCents,
    })) as Product[];
    return {
      data: rows,
      total: Array.isArray(res) ? rows.length : (res?.total ?? rows.length),
      page: Array.isArray(res) ? 1 : (res?.page ?? params?.page ?? 1),
      limit: Array.isArray(res) ? rows.length : (res?.limit ?? params?.limit ?? 20),
    };
  },

  /** Full product (all fields) for the detail modal. Spring Boot, needs business access. */
  getProduct: (businessId: string, productId: string) =>
    writesApi.get<any>(`/businesses/${businessId}/products/${productId}`),

  suspendProduct: (businessId: string, productId: string) =>
    writesApi.patch(`/businesses/${businessId}/products/${productId}/suspend`),

  activateProduct: (businessId: string, productId: string) =>
    writesApi.patch(`/businesses/${businessId}/products/${productId}/activate`),

  removeProduct: (businessId: string, productId: string) =>
    writesApi.delete(`/businesses/${businessId}/products/${productId}`),

  updateProduct: (businessId: string, branchId: string, productId: string, data: Record<string, unknown>) =>
    writesApi.put<Product>(
      `/businesses/${businessId}/branches/${branchId}/products/${productId}`,
      data,
    ),

  /** Reviewer view of onboarding progress. Spring Boot, requires businesses.read. */
  getOnboarding: (businessId: string) =>
    writesApi.get<BusinessOnboarding>(`/admin/businesses/${businessId}/onboarding`),

};

export interface OnboardingStep {
  key: string;
  title: string;
  description: string;
  done: boolean;
  optional: boolean;
}

export interface BusinessOnboarding {
  businessId: string;
  businessName: string;
  steps: OnboardingStep[];
  requiredTotal: number;
  requiredDone: number;
  progressPct: number;
  goLive: { live: boolean; status: string; shopUrl: string | null };
  dismissed: boolean;
  signals: {
    selectedCategories?: string[];
    categorySatisfaction?: number;
    categoryFeedback?: string;
    calculatorUsedAt?: string;
  };
  engagement: {
    first_seen_at?: string | null;
    last_activity_at?: string | null;
    activated_at?: string | null;
    go_live_celebrated_at?: string | null;
    nudge_count?: number | null;
    last_nudge_at?: string | null;
  };
}
